import { CHANNEL_LABELS, fmtDate } from '@jt/core';
import { Link } from 'react-router-dom';
import type { ApplicationListItem } from '@jt/shared';
import { useQueryClient } from '@tanstack/react-query';
import { post } from '../api/client';
import { keys, refreshSummaries, useApplications, useRecent, useToday, useTodayDate } from '../api/hooks';
import { useUI } from '../components/overlays';
import { Icon, Logo, PageHead } from '../components/ui';
import { openPaste } from '../drafts/openPaste';
import { useOpenDraft } from '../drafts/useDraft';
import { InboxBlock } from '../inbox/InboxBlock';
import { CHANNEL_ICON } from '../panel/History';

function ReplyRow({ a }: { a: ApplicationListItem }) {
  const ui = useUI();
  const qc = useQueryClient();
  const today = useTodayDate();
  const noReply = async () => {
    try {
      await post(`/applications/${a.id}/activities`, {
        occurredOn: today,
        channel: 'System',
        direction: 'out',
        text: 'Отмечено: ответ не требуется',
      });
      await qc.invalidateQueries({ queryKey: keys.apps() });
      void qc.invalidateQueries({ queryKey: keys.app(a.id) });
      refreshSummaries(qc);
      ui.toast('Убрано из «Ждут вашего ответа»');
    } catch {
      ui.toast('Не сохранилось. Повторите.');
    }
  };
  const openDraft = useOpenDraft();
  const ai = !!useToday().data?.ai;
  const l = a.lastMessage;
  return (
    <div className="lrow">
      <Logo name={a.company} size={32} />
      <div
        className="lm clickable"
        role="button"
        tabIndex={0}
        onClick={() => ui.openApp(a.id)}
        onKeyDown={(e) => e.key === 'Enter' && ui.openApp(a.id)}
      >
        <b>{a.company}</b> <span className="muted">{a.role}</span>
        {l && (
          <>
            <div className="meta">
              {CHANNEL_LABELS[l.channel]} · {fmtDate(l.occurredOn)}
            </div>
            <div className="txt">{l.text}</div>
          </>
        )}
      </div>
      <div className="btnrow" style={{ flexWrap: 'nowrap' }}>
        {ai && (
          <button className="btn sm" onClick={() => openDraft(a.id, 'reply')}>
            Черновик
          </button>
        )}
        <button
          className="btn sm ghost"
          onClick={() => void noReply()}
          title="Автоответ или отвечать не нужно"
        >
          Ответ не нужен
        </button>
      </div>
    </div>
  );
}

export function MessagesPage() {
  const ui = useUI();
  const apps = useApplications();
  const recent = useRecent();
  const ai = !!useToday().data?.ai;
  const reply = (apps.data ?? []).filter((a) => a.needsReply);
  return (
    <>
      <PageHead title="Сообщения" sub="Почта и переписка по откликам">
        {ai && (
          <button className="btn" onClick={() => openPaste(ui, null)}>
            <Icon name="clip" /> Вставить сообщение
          </button>
        )}
      </PageHead>
      <InboxBlock />
      {reply.length > 0 && (
        <section className="box">
          <h2>Ждут вашего ответа · {reply.length}</h2>
          <div className="list">
            {reply.map((a) => (
              <ReplyRow key={a.id} a={a} />
            ))}
          </div>
        </section>
      )}
      <section className="box">
        <h2>Последняя переписка</h2>
        {recent.data?.length ? (
          <div className="list">
            {recent.data.map((l) => (
              <div
                key={l.id}
                className="lrow clickable"
                role="button"
                tabIndex={0}
                onClick={() => ui.openApp(l.applicationId)}
                onKeyDown={(e) => e.key === 'Enter' && ui.openApp(l.applicationId)}
              >
                <span className="evic">
                  <Icon name={CHANNEL_ICON[l.channel]} />
                </span>
                <div className="lm">
                  <b>{l.company}</b>{' '}
                  <span className="muted">{l.direction === 'in' ? '→ вам' : '← от вас'}</span>
                  <div className="meta">
                    {CHANNEL_LABELS[l.channel]} · {fmtDate(l.occurredOn)}
                  </div>
                  <div className="txt">{l.text}</div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="muted" style={{ margin: 0 }}>
            Переписки пока нет. Добавляйте записи в карточках или разбирайте письма —{' '}
            <Link to="/settings#gmail">подключите Gmail</Link>.
          </p>
        )}
      </section>
    </>
  );
}
